/**
 * GettingStartedPill.jsx — v3.7.15 Phase 84.32 Part 1
 *
 * Compact "Getting started · 4 of 5" pill that replaces the full-width
 * ActivationChecklist card at the top of the dashboard.
 *
 * Behaviour
 * ---------
 * - Renders the current progress (`{done} of {total}`) with a tiny
 *   segmented bar.
 * - Once the user has hit 5/5 AND acknowledged, the pill disappears
 *   (localStorage flag `twoone20:getStartedAckedAt`). We never keep
 *   showing "5 of 5" indefinitely.
 * - Never renders when we don't have activation signals yet
 *   (loading state) — parent decides visibility.
 */
import React, { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';

export const GETTING_STARTED_ACK_KEY = 'twoone20:getStartedAckedAt';

export function computeChecklistProgress(a = {}) {
  const steps = [
    { key: 'account',     done: !!a.hasAccount },
    { key: 'import',      done: !!a.hasImported },
    { key: 'categorise',  done: !!a.hasCategorised },
    { key: 'priority',    done: !!a.hasGoal },
    { key: 'first_action',done: !!a.hasReflection },
  ];
  const done = steps.filter((s) => s.done).length;
  return { steps, done, total: steps.length, complete: done >= steps.length };
}

export default function GettingStartedPill({ activation, onOpen }) {
  const { done, total, complete } = useMemo(
    () => computeChecklistProgress(activation),
    [activation],
  );

  // Once acknowledged after full completion, don't render.
  const acked = (() => {
    try { return !!window.localStorage.getItem(GETTING_STARTED_ACK_KEY); }
    catch { return false; }
  })();
  if (complete && acked) return null;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={
        'inline-flex items-center gap-2 h-9 px-3 rounded-full border text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ' +
        (complete
          ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100/70'
          : 'bg-muted/50 border-border/70 hover:bg-muted text-charcoal')
      }
      aria-label={complete ? 'Getting started complete' : `Getting started, ${done} of ${total} steps completed`}
      data-testid="getting-started-pill"
    >
      <span aria-hidden="true">{complete ? '✓ All set up' : 'Getting started'}</span>
      {!complete && (
        <>
          <span className="tabular-nums text-muted-foreground">{done}/{total}</span>
          <span className="hidden sm:flex items-center gap-0.5" aria-hidden="true">
            {Array.from({ length: total }).map((_, i) => (
              <span
                key={i}
                className={
                  'h-1.5 w-2 rounded-full ' +
                  (i < done ? 'bg-primary' : 'bg-border')
                }
              />
            ))}
          </span>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </>
      )}
    </button>
  );
}
